import type { DiamondInput } from './diamond-config';

export type DiamondProfile = { slug: string; name: string; input: DiamondInput };

const shapes: { value: DiamondInput['shape']; name: string }[] = [
  { value: 'round', name: 'Round' },
  { value: 'oval', name: 'Oval' },
  { value: 'emerald', name: 'Emerald' },
];
const colors: DiamondInput['color'][] = ['D', 'E', 'F', 'G', 'H', 'I'];
const clarities: DiamondInput['clarity'][] = ['VVS1', 'VVS2', 'VS1', 'VS2'];
const qualities = colors.flatMap(color => clarities.map(clarity => ({ color, clarity })));

/** Bounded price configurations, ordered by carat, shape, color and clarity. */
export const DIAMOND_PROFILES: DiamondProfile[] = [0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2, 2.5, 3].flatMap(carat => shapes.flatMap(shape => qualities.map(({ color, clarity }) => ({
  slug: `${shape.value}-${carat.toFixed(2).replace('.', '-')}-carat-${color.toLowerCase()}-${clarity.toLowerCase()}`,
  name: `Kim cương ${shape.name} ${carat.toFixed(2).replace('.', ',')} carat ${color}/${clarity}`,
  input: { shape: shape.value, carat, color, clarity, cut: 'excellent', symmetry: 'excellent', polish: 'excellent', fluorescence: 'none' },
}))));

export const DIAMOND_PAGE_SIZE = 6;
export const DIAMOND_PAGE_COUNT = Math.ceil(DIAMOND_PROFILES.length / DIAMOND_PAGE_SIZE);

export const getDiamondProfile = (slug: unknown): DiamondProfile | null => typeof slug === 'string'
  ? DIAMOND_PROFILES.find(profile => profile.slug === slug) || null : null;

export const getDiamondPage = (page: number): DiamondProfile[] | null => {
  if (!Number.isInteger(page) || page < 1 || page > DIAMOND_PAGE_COUNT) return null;
  return DIAMOND_PROFILES.slice((page - 1) * DIAMOND_PAGE_SIZE, page * DIAMOND_PAGE_SIZE);
};

/** Route parameters must have a single canonical integer representation. */
export const parseDiamondPage = (value: unknown): number | null => {
  if (typeof value !== 'string' || !/^[1-9]\d*$/.test(value)) return null;
  const page = Number(value);
  return getDiamondPage(page) ? page : null;
};

export const diamondProfilePath = (slug: string): string => `/bang-gia/kim-cuong/${slug}/`;

export const diamondCataloguePath = (page: number): string => page === 1
  ? '/bang-gia/kim-cuong/' : `/bang-gia/kim-cuong/trang/${page}/`;

/** Keep long catalogues navigable without rendering every page number. */
export const diamondPaginationPages = (page: number, pageCount: number): (number | 'ellipsis')[] => {
  if (!Number.isSafeInteger(page) || !Number.isSafeInteger(pageCount) || pageCount < 1 || page < 1 || page > pageCount) return [];
  const candidates = [1, pageCount, page - 1, page, page + 1];
  if (page === 1) candidates.push(3);
  if (page === pageCount) candidates.push(pageCount - 2);
  const pages = Array.from(new Set(candidates.filter(value => value >= 1 && value <= pageCount))).sort((a, b) => a - b);
  const result: (number | 'ellipsis')[] = [];
  pages.forEach((value, index) => {
    if (index > 0 && value - pages[index - 1] > 1) result.push('ellipsis');
    result.push(value);
  });
  return result;
};
